import React, { useEffect, useRef, useState, forwardRef, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    numberSeriesCreate,
    numberSeriesList,
    numberSeriesUpdate
} from '../../../../../redux/actions/action-creator';
import './define.css';
import { getVoucherDate, dateFormateSend, dateFormateShow } from '../../../../../components/Date_Formate_Utilitiy/Index';

const DefineNumberSeries = forwardRef((props, ref) => {
    const dispatch = useDispatch();
    const { userData = null } = useSelector(state => state.auth);
    const { company_session = null } = useSelector(state => state.common || {});

    React.useImperativeHandle(ref, () => ({
        submit: controlPlusASubmit,
        quit: handleQuit
    }));

    const initialState = {
        series_name: props.name || '',
        method_of_voucher_numbering: 'Automatic',
        numbering_behaviour_on_insertion_deletion: 'Retain Original Voucher No.',
        set_alter_additional_numbering_details: 'Yes',
        prevent_creating_duplicate_voucher_nos: 'No',
        starting_number: '1',
        width_of_numerical_part: '',
        prefill_with_zero: 'No',
        applicable_from_restart_numbering: '',
        starting_number_restarting_numbering: '',
        periodicity: '',
        applicable_from_prefix_details: '',
        particulars_prefix_details: '',
        applicable_from_suffix_details: '',
        particulars_suffix_details: '',
    };

    const [state, setState] = useState(initialState);
    const [existingId, setExistingId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [prefillDropdown, setPrefillDropdown] = useState(false);
    const [selectedItem, setSelectedItem] = useState(0);

    // Input Refs for sequential navigation
    const starting_number_ref = useRef(null);
    const width_ref = useRef(null);
    const prefill_ref = useRef(null);
    const app_restart_ref = useRef(null);
    const start_restart_ref = useRef(null);
    const periodicity_ref = useRef(null);
    const app_prefix_ref = useRef(null);
    const part_prefix_ref = useRef(null);
    const app_suffix_ref = useRef(null);
    const part_suffix_ref = useRef(null);

    const getNextRef = (name) => {
        switch (name) {
            case 'starting_number': return width_ref;
            case 'width_of_numerical_part': return prefill_ref;
            case 'prefill_with_zero': return app_restart_ref;
            case 'applicable_from_restart_numbering': return start_restart_ref;
            case 'starting_number_restarting_numbering': return periodicity_ref;
            case 'periodicity': return app_prefix_ref;
            case 'applicable_from_prefix_details': return part_prefix_ref;
            case 'particulars_prefix_details': return app_suffix_ref;
            case 'applicable_from_suffix_details': return part_suffix_ref;
            default: return null;
        }
    };

    const getPrevRef = (name) => {
        switch (name) {
            case 'width_of_numerical_part': return starting_number_ref;
            case 'prefill_with_zero': return width_ref;
            case 'applicable_from_restart_numbering': return prefill_ref;
            case 'starting_number_restarting_numbering': return app_restart_ref;
            case 'periodicity': return start_restart_ref;
            case 'applicable_from_prefix_details': return periodicity_ref;
            case 'particulars_prefix_details': return app_prefix_ref;
            case 'applicable_from_suffix_details': return part_prefix_ref;
            case 'particulars_suffix_details': return app_suffix_ref;
            default: return null;
        }
    };

    // Load configuration on mount
    useEffect(() => {
        const formData = new FormData();
        formData.append('voucher_type_name', props.name);
        formData.append('company_id', userData?.id);
        dispatch(numberSeriesList(formData)).then((response) => {
            if (response.status && response.data && response.data.length > 0) {
                const existingSeries = response.data[0];
                setExistingId(existingSeries.id);
                setState({
                    series_name: existingSeries.series_name || props.name,
                    method_of_voucher_numbering: 'Automatic',
                    numbering_behaviour_on_insertion_deletion: existingSeries.numbering_behaviour || 'Retain Original Voucher No.',
                    set_alter_additional_numbering_details: 'Yes',
                    prevent_creating_duplicate_voucher_nos: 'No',
                    starting_number: existingSeries.starting_number || '1',
                    width_of_numerical_part: existingSeries.width_of_numerical_part || '',
                    prefill_with_zero: existingSeries.prefill_with_zero || 'No',
                    applicable_from_restart_numbering: existingSeries.applicable_from && existingSeries.applicable_from !== '1970-01-01' ? dateFormateShow(existingSeries.applicable_from) : '',
                    starting_number_restarting_numbering: existingSeries.starting_number_2 || '',
                    periodicity: existingSeries.periodicity || '',
                    applicable_from_prefix_details: existingSeries.applicable_from_prefix && existingSeries.applicable_from_prefix !== '1970-01-01' ? dateFormateShow(existingSeries.applicable_from_prefix) : '',
                    particulars_prefix_details: existingSeries.prefix_particular || '',
                    applicable_from_suffix_details: existingSeries.applicable_from_suffix && existingSeries.applicable_from_suffix !== '1970-01-01' ? dateFormateShow(existingSeries.applicable_from_suffix) : '',
                    particulars_suffix_details: existingSeries.suffix_particular || '',
                });
            } else {
                setState(prev => ({
                    ...prev,
                    series_name: props.name || '',
                }));
            }
            setLoading(false);
            setTimeout(() => {
                starting_number_ref.current?.focus();
            }, 100);
        }).catch((err) => {
            console.error(err);
            setLoading(false);
        });
    }, [props.name, userData?.id, dispatch]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setState(prev => ({ ...prev, [name]: value }));
    };

    const handleFocus = (e) => {
        const { name } = e.target;
        if (name === 'prefill_with_zero') {
            setPrefillDropdown(true);
            setSelectedItem(state.prefill_with_zero === 'Yes' ? 0 : 1);
        }
    };

    const handleBlur = (e) => {
        const { name } = e.target;
        if (name === 'prefill_with_zero') {
            setTimeout(() => setPrefillDropdown(false), 200);
        }
    };

    const handleDateInput = (name, value, currentRef, nextRef, e) => {
        if (!value) {
            if (e) e.preventDefault();
            if (nextRef && nextRef.current) {
                nextRef.current.focus();
            }
            return true;
        }

        const dateResult = getVoucherDate(value, company_session);
        if (dateResult && dateResult.formattedDateString) {
            setState(prev => ({ ...prev, [name]: dateResult.formattedDateString }));
            if (e) e.preventDefault();
            if (nextRef && nextRef.current) {
                nextRef.current.focus();
            }
            return true;
        } else {
            if (e) {
                e.preventDefault();
                e.stopPropagation();
            }
            toast.error('Invalid date. Try: 1-4-26 or 1-Apr-2026', { position: toast.POSITION.TOP_CENTER });
            currentRef.current?.focus();
            currentRef.current?.select();
            return false;
        }
    };

    const handleDateBlur = (name, value) => {
        if (!value) return;
        const dateResult = getVoucherDate(value, company_session);
        if (dateResult && dateResult.formattedDateString) {
            setState(prev => ({ ...prev, [name]: dateResult.formattedDateString }));
        }
    };

    const handleKeyDown = (e) => {
        const { name, value } = e.target;

        if (name === 'prefill_with_zero' && prefillDropdown) {
            if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                e.preventDefault();
                setSelectedItem(prev => prev === 0 ? 1 : 0);
                return;
            }
            if (e.key === 'Enter') {
                e.preventDefault();
                const selectedVal = selectedItem === 0 ? 'Yes' : 'No';
                setState(prev => ({ ...prev, prefill_with_zero: selectedVal }));
                setPrefillDropdown(false);
                app_restart_ref.current?.focus();
                return;
            }
        }

        const dateFields = [
            'applicable_from_restart_numbering',
            'applicable_from_prefix_details',
            'applicable_from_suffix_details'
        ];

        if (e.key === 'Enter' || e.key === 'Tab') {
            if (dateFields.includes(name)) {
                const currentRef = name === 'applicable_from_restart_numbering' ? app_restart_ref :
                                   name === 'applicable_from_prefix_details' ? app_prefix_ref : app_suffix_ref;
                const nextRef = getNextRef(name);
                
                const ok = handleDateInput(name, value, currentRef, nextRef, e);
                if (!ok) {
                    return;
                }
            } else if (e.key === 'Enter') {
                e.preventDefault();
                const nextRef = getNextRef(name);
                if (nextRef && nextRef.current) {
                    nextRef.current.focus();
                } else if (name === 'particulars_suffix_details') {
                    controlPlusASubmit();
                }
            }
        } else if (e.key === 'Backspace' && e.target.selectionStart === 0) {
            const prevRef = getPrevRef(name);
            if (prevRef && prevRef.current) {
                e.preventDefault();
                prevRef.current.focus();
            }
        }
    };

    const controlPlusASubmit = async () => {
        try {
            const data = new FormData();
            data.append('company_id', userData?.id);
            data.append('voucher_type_name', props.name);
            data.append('series_name', state.series_name);
            data.append('method_of_voucher_numbering', state.method_of_voucher_numbering);
            data.append('numbering_behaviour', state.numbering_behaviour_on_insertion_deletion);
            data.append('set_additional_numbering', state.set_alter_additional_numbering_details);
            data.append('starting_number', state.starting_number);
            data.append('width_of_numerical_part', state.width_of_numerical_part);
            data.append('prefill_with_zero', state.prefill_with_zero);
            data.append('applicable_from', state.applicable_from_restart_numbering ? dateFormateSend(state.applicable_from_restart_numbering) : '');
            data.append('starting_number_2', state.starting_number_restarting_numbering);
            data.append('periodicity', state.periodicity);
            data.append('applicable_from_prefix', state.applicable_from_prefix_details ? dateFormateSend(state.applicable_from_prefix_details) : '');
            data.append('prefix_particular', state.particulars_prefix_details);
            data.append('applicable_from_suffix', state.applicable_from_suffix_details ? dateFormateSend(state.applicable_from_suffix_details) : '');
            data.append('suffix_particular', state.particulars_suffix_details);
            data.append('prevent_creating_duplicate_voucher', state.prevent_creating_duplicate_voucher_nos);

            let response;
            if (existingId) {
                data.append('id', existingId);
                response = await dispatch(numberSeriesUpdate(data));
            } else {
                response = await dispatch(numberSeriesCreate(data));
            }

            if (response.status) {
                toast.success(response?.message || 'Numbering details saved!', {
                    position: toast.POSITION.TOP_CENTER
                });
                
                // Sync parent state
                props.setState(prev => ({
                    ...prev,
                    voucher_number_periodicity: state.periodicity,
                    voucher_number_prefix: state.particulars_prefix_details,
                    voucher_number_suffix: state.particulars_suffix_details,
                }));
                
                props.setDefineNumberModal(false);
                if (props.ref_name && props.ref_name.current) {
                    props.ref_name.current.focus();
                }
            } else {
                toast.warn(response?.message || 'Failed to save configuration.', {
                    position: toast.POSITION.TOP_CENTER
                });
            }
        } catch (error) {
            console.error(error);
            toast.error(error?.message || 'Failed to save configuration.', {
                position: toast.POSITION.TOP_CENTER
            });
        }
    };

    const handleQuit = () => {
        props.setDefineNumberModal(false);
        if (props.ref_name && props.ref_name.current) {
            props.ref_name.current.focus();
        }
    };

    // Keyboard Shortcuts
    useEffect(() => {
        const handleGlobalKeyDown = (e) => {
            if (e.key === 'Escape') {
                e.preventDefault();
                handleQuit();
            } else if (e.key === 'a' && e.ctrlKey) {
                e.preventDefault();
                controlPlusASubmit();
            }
        };
        window.addEventListener('keydown', handleGlobalKeyDown);
        return () => {
            window.removeEventListener('keydown', handleGlobalKeyDown);
        };
    }, [state, existingId]);

    if (loading) {
        return (
            <div className='number-series-details-overlay'>
                <div className='number-series-details-card' style={{ zIndex: 11000, height: '100%', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#fff', border: '1px solid #aaa' }}>
                    <h3>Loading Numbering Details...</h3>
                </div>
            </div>
        );
    }

    return (
        <div className='number-series-details-overlay'>
            <div className='number-series-details-card' style={{ zIndex: 11000, height: '100%', width: '100%', display: 'flex', flexDirection: 'column', position: 'relative', background: '#fff', border: '1px solid #aaa', padding: 0 }}>
                
                <div className='modal-header-bar'>
                    <span className='modal-header-title'>
                        Additional numbering details
                    </span>
                    <span className='modal-header-close' onClick={handleQuit}>&times;</span>
                </div>

                <div className='modal-body-content' style={{ padding: '15px', display: 'flex', flexDirection: 'column', flexGrow: 1, backgroundColor: '#ffffff', overflowY: 'auto' }}>
                    
                    <div className="number-series-top-fields" style={{ width: '400px', marginBottom: '5px' }}>
                        {/* Starting Number */}
                        <div className="number-series-field" style={{ display: 'flex', alignItems: 'center', marginBottom: '1px' }}>
                            <span className="field-label" style={{ width: '180px', fontSize: '13px' }}>Starting Number</span>
                            <span className="field-colon" style={{ width: '15px', textAlign: 'center' }}>:</span>
                            <div className="field-input-wrapper" style={{ flexGrow: 1 }}>
                                <input
                                    type="text"
                                    name='starting_number'
                                    id='starting_number'
                                    onChange={handleChange}
                                    onKeyDown={handleKeyDown}
                                    ref={starting_number_ref}
                                    value={state.starting_number || ""}
                                />
                            </div>
                        </div>

                        {/* Width of Numerical Part */}
                        <div className="number-series-field" style={{ display: 'flex', alignItems: 'center', marginBottom: '1px' }}>
                            <span className="field-label" style={{ width: '180px', fontSize: '13px' }}>Width of Numerical Part</span>
                            <span className="field-colon" style={{ width: '15px', textAlign: 'center' }}>:</span>
                            <div className="field-input-wrapper" style={{ flexGrow: 1 }}>
                                <input
                                    type="text"
                                    name='width_of_numerical_part'
                                    id='width_of_numerical_part'
                                    onChange={handleChange}
                                    onKeyDown={handleKeyDown}
                                    ref={width_ref}
                                    value={state.width_of_numerical_part || ""}
                                />
                            </div>
                        </div>

                        {/* Prefill with zero */}
                        <div className="number-series-field" style={{ display: 'flex', alignItems: 'center', marginBottom: '1px', position: 'relative' }}>
                            <span className="field-label" style={{ width: '180px', fontSize: '13px' }}>Prefill with zero</span>
                            <span className="field-colon" style={{ width: '15px', textAlign: 'center' }}>:</span>
                            <div className="field-input-wrapper" style={{ flexGrow: 1 }}>
                                <input
                                    type="text"
                                    name='prefill_with_zero'
                                    id='prefill_with_zero'
                                    onChange={handleChange}
                                    onFocus={handleFocus}
                                    onBlur={handleBlur}
                                    onKeyDown={handleKeyDown}
                                    ref={prefill_ref}
                                    value={state.prefill_with_zero || ""}
                                />
                                {prefillDropdown && (
                                    <div id="myDropdown-list" className="dropdown_yes_no_list show-list" style={{
                                        position: 'absolute',
                                        top: '22px',
                                        left: '0px',
                                        backgroundColor: '#f6f6f6',
                                        border: '1px solid #000',
                                        zIndex: 100,
                                        width: '100%',
                                        height: 'auto',
                                        overflow: 'hidden'
                                    }}>
                                        <div className="dropdown_header" style={{ background: '#162070', color: '#fff', padding: '2px 5px', fontSize: '12px', fontWeight: 'bold' }}>
                                            Yes / No
                                        </div>
                                        <div id="data" style={{ overflow: 'hidden' }}>
                                            {['Yes', 'No'].map((opt, idx) => {
                                                const isSelected = idx === selectedItem;
                                                return (
                                                    <Link
                                                        key={opt}
                                                        className={`list font-weight-bold ${isSelected ? 'selected' : ''}`}
                                                        style={{
                                                            textDecoration: 'none',
                                                            display: 'block',
                                                            color: '#000',
                                                            padding: '4px 10px',
                                                            fontSize: '13px'
                                                        }}
                                                        onClick={() => {
                                                            setState(prev => ({ ...prev, prefill_with_zero: opt }));
                                                            setPrefillDropdown(false);
                                                            app_restart_ref.current?.focus();
                                                        }}
                                                    >
                                                        {opt}
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Table Grid */}
                    <div className="number-series-grid" style={{ display: 'flex', border: '1px solid #aaa', flexGrow: 1, backgroundColor: '#fff', height: '70vh' }}>
                        
                        {/* Restart Numbering Section */}
                        <div className="grid-section restart-numbering-section" style={{ width: '40%', borderRight: '1px solid #aaa', display: 'flex', flexDirection: 'column' }}>
                            <div className="section-header" style={{ borderBottom: '1px solid #aaa', padding: '5px', textAlign: 'center', fontWeight: 'bold' }}>Restart Numbering</div>
                            <div className="section-subheader-row" style={{ display: 'flex', borderBottom: '1px solid #aaa', backgroundColor: '#f9f9f9' }}>
                                <div className="subheader-cell" style={{ flex: 1, padding: '4px', borderRight: '1px solid #aaa', textAlign: 'center', fontSize: '11px' }}>Applicable From</div>
                                <div className="subheader-cell" style={{ flex: 1, padding: '4px', borderRight: '1px solid #aaa', textAlign: 'center', fontSize: '11px' }}>Starting Number</div>
                                <div className="subheader-cell" style={{ flex: 1, padding: '4px', textAlign: 'center', fontSize: '11px' }}>Periodicity</div>
                            </div>
                            <div className="section-input-row" style={{ display: 'flex', flexGrow: 1 }}>
                                <div className="input-cell" style={{ flex: 1, borderRight: '1px solid #aaa' }}>
                                    <input
                                        type="text"
                                        name='applicable_from_restart_numbering'
                                        onChange={handleChange}
                                        onKeyDown={handleKeyDown}
                                        onBlur={(e) => handleDateBlur(e.target.name, e.target.value)}
                                        ref={app_restart_ref}
                                        value={state.applicable_from_restart_numbering || ""}
                                        autoComplete="off"
                                    />
                                </div>
                                <div className="input-cell" style={{ flex: 1, borderRight: '1px solid #aaa' }}>
                                    <input
                                        type="text"
                                        name='starting_number_restarting_numbering'
                                        onChange={handleChange}
                                        onKeyDown={handleKeyDown}
                                        ref={start_restart_ref}
                                        value={state.starting_number_restarting_numbering || ""}
                                    />
                                </div>
                                <div className="input-cell" style={{ flex: 1 }}>
                                    <input
                                        type="text"
                                        name='periodicity'
                                        onChange={handleChange}
                                        onKeyDown={handleKeyDown}
                                        ref={periodicity_ref}
                                        value={state.periodicity || ""}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Prefix Details Section */}
                        <div className="grid-section prefix-details-section" style={{ width: '30%', borderRight: '1px solid #aaa', display: 'flex', flexDirection: 'column' }}>
                            <div className="section-header" style={{ borderBottom: '1px solid #aaa', padding: '5px', textAlign: 'center', fontWeight: 'bold' }}>Prefix Details</div>
                            <div className="section-subheader-row" style={{ display: 'flex', borderBottom: '1px solid #aaa', backgroundColor: '#f9f9f9' }}>
                                <div className="subheader-cell" style={{ flex: 1, padding: '4px', borderRight: '1px solid #aaa', textAlign: 'center', fontSize: '11px' }}>Applicable From</div>
                                <div className="subheader-cell" style={{ flex: 1, padding: '4px', textAlign: 'center', fontSize: '11px' }}>Particulars</div>
                            </div>
                            <div className="section-input-row" style={{ display: 'flex', flexGrow: 1 }}>
                                <div className="input-cell" style={{ flex: 1, borderRight: '1px solid #aaa' }}>
                                    <input
                                        type="text"
                                        name='applicable_from_prefix_details'
                                        onChange={handleChange}
                                        onKeyDown={handleKeyDown}
                                        onBlur={(e) => handleDateBlur(e.target.name, e.target.value)}
                                        ref={app_prefix_ref}
                                        value={state.applicable_from_prefix_details || ""}
                                        autoComplete="off"
                                    />
                                </div>
                                <div className="input-cell" style={{ flex: 1 }}>
                                    <input
                                        type="text"
                                        name='particulars_prefix_details'
                                        onChange={handleChange}
                                        onKeyDown={handleKeyDown}
                                        ref={part_prefix_ref}
                                        value={state.particulars_prefix_details || ""}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Suffix Details Section */}
                        <div className="grid-section suffix-details-section" style={{ width: '30%', display: 'flex', flexDirection: 'column' }}>
                            <div className="section-header" style={{ borderBottom: '1px solid #aaa', padding: '5px', textAlign: 'center', fontWeight: 'bold' }}>Suffix Details</div>
                            <div className="section-subheader-row" style={{ display: 'flex', borderBottom: '1px solid #aaa', backgroundColor: '#f9f9f9' }}>
                                <div className="subheader-cell" style={{ flex: 1, padding: '4px', borderRight: '1px solid #aaa', textAlign: 'center', fontSize: '11px' }}>Applicable From</div>
                                <div className="subheader-cell" style={{ flex: 1, padding: '4px', textAlign: 'center', fontSize: '11px' }}>Particulars</div>
                            </div>
                            <div className="section-input-row" style={{ display: 'flex', flexGrow: 1 }}>
                                <div className="input-cell" style={{ flex: 1, borderRight: '1px solid #aaa' }}>
                                    <input
                                        type="text"
                                        name='applicable_from_suffix_details'
                                        onChange={handleChange}
                                        onKeyDown={handleKeyDown}
                                        onBlur={(e) => handleDateBlur(e.target.name, e.target.value)}
                                        ref={app_suffix_ref}
                                        value={state.applicable_from_suffix_details || ""}
                                        autoComplete="off"
                                    />
                                </div>
                                <div className="input-cell" style={{ flex: 1 }}>
                                    <input
                                        type="text"
                                        name='particulars_suffix_details'
                                        onChange={handleChange}
                                        onKeyDown={handleKeyDown}
                                        ref={part_suffix_ref}
                                        value={state.particulars_suffix_details || ""}
                                    />
                                </div>
                            </div>
                        </div>

                    </div>
                </div>



            </div>
        </div>
    );
});

export default DefineNumberSeries;
